<script>
	import { onDestroy } from "svelte";
	import {
		faBackwardStep,
		faForwardStep,
		faPauseCircle,
		faPlayCircle,
	} from "@fortawesome/free-solid-svg-icons";
	import SvelteFa from "$lib/components/SvelteFa.svelte";

	export let src = "";
	export let waveforms = [];
	export let transcript = [];

	let audioRef;
	let waveformRef;
	let currentTime = 0;
	let duration = 0;
	let progress = 0;
	let isPlaying = false;
	let isSeeking = false;
	let segmentRefs = [];
	let transcriptContainer;
	let activeSegmentIndex = -1;

	const clamp = (value, min = 0, max = 1) =>
		Math.min(max, Math.max(min, value));

	const syncFromAudio = () => {
		if (!audioRef) return;
		currentTime = audioRef.currentTime || 0;
		duration = audioRef.duration || 0;
		progress = duration ? currentTime / duration : 0;
	};

	const setTime = (time) => {
		duration = audioRef?.duration || 0;
		const clamped = clamp(time, 0, duration || 0);
		if (audioRef) audioRef.currentTime = clamped;
		currentTime = clamped;
		progress = duration ? clamped / duration : 0;
		updateActiveSegment();
	};

	const formatTime = (sec = 0) => {
		if (!Number.isFinite(sec)) return "0:00";
		const minutes = Math.max(0, Math.floor(sec / 60));
		const seconds = Math.max(0, Math.floor(sec % 60));
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	const playPause = () => {
		if (!audioRef) return;
		if (audioRef.paused) {
			audioRef.play();
		} else {
			audioRef.pause();
		}
		isPlaying = !audioRef.paused;
	};

	const jumpBy = (seconds) => {
		setTime(currentTime + seconds);
	};

	const handleAudioPlay = () => {
		isPlaying = true;
	};

	const handleAudioPause = () => {
		isPlaying = false;
	};

	const handleTimeUpdate = () => {
		if (isSeeking) return;
		syncFromAudio();
		updateActiveSegment();
	};

	const handleLoadedMetadata = () => {
		syncFromAudio();
	};

	const setTimeFromClientX = (clientX) => {
		if (!waveformRef || !duration) return;
		const rect = waveformRef.getBoundingClientRect();
		const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
		setTime(ratio * duration);
	};

	const pointerMove = (event) => {
		if (!isSeeking) return;
		setTimeFromClientX(event.clientX);
		updateActiveSegment();
	};

	const pointerUp = (event) => {
		if (isSeeking) setTimeFromClientX(event.clientX);
		isSeeking = false;
		window.removeEventListener("pointermove", pointerMove);
		window.removeEventListener("pointerup", pointerUp);
		updateActiveSegment();
	};

	const handleSeekStart = (event) => {
		if (!duration) return;
		isSeeking = true;
		setTimeFromClientX(event.clientX);
		window.addEventListener("pointermove", pointerMove);
		window.addEventListener("pointerup", pointerUp);
		updateActiveSegment();
	};

	const updateActiveSegment = () => {
		if (!Array.isArray(transcript) || !transcript.length) {
			activeSegmentIndex = -1;
			return;
		}
		const idx = findSegmentIndex(currentTime, transcript);
		if (idx !== activeSegmentIndex) {
			activeSegmentIndex = idx;
			scrollSegmentIntoView(idx);
		}
	};

	const scrollSegmentIntoView = (index) => {
		const container = transcriptContainer;
		const target = segmentRefs[index];
		if (!container || !target) return;
		const relativeTop = target.offsetTop - container.offsetTop;
		const padding = 96;
		container.scrollTo({
			top: Math.max(0, relativeTop - padding),
			behavior: "smooth",
		});
	};

	const findSegmentIndex = (time, segments) => {
		if (!segments.length) return -1;
		for (let i = 0; i < segments.length; i += 1) {
			const start = Number(segments[i]?.start ?? 0);
			const next = Number(segments[i + 1]?.start ?? Infinity);
			if (time >= start && time < next) return i;
		}
		return segments.length - 1;
	};

	onDestroy(() => {
		window.removeEventListener("pointermove", pointerMove);
		window.removeEventListener("pointerup", pointerUp);
	});

	const formatSegmentTime = (seconds) => {
		if (typeof seconds !== "number" || !Number.isFinite(seconds)) return "";
		const mins = Math.floor(seconds / 60)
			.toString()
			.padStart(2, "0");
		const secs = Math.floor(seconds % 60)
			.toString()
			.padStart(2, "0");
		return `${mins}:${secs}`;
	};

	const formatSpeaker = (speaker) => {
		if (!speaker) return "Unknown";
		return speaker
			.replace(/_/g, " ")
			.replace(/\b\w/g, (match) => match.toUpperCase());
	};

	const getBubbleClasses = (speaker) =>
		speaker?.toLowerCase() === "caller"
			? "chat chat-end"
			: "chat chat-start";

	const getBubbleInnerClasses = (speaker) =>
		speaker?.toLowerCase() === "caller"
			? "chat-bubble bg-primary text-primary-content"
			: "chat-bubble bg-base-300 text-base-content";
</script>

<div class="space-y-3">
	<audio
		class="sr-only"
		{src}
		bind:this={audioRef}
		on:timeupdate={handleTimeUpdate}
		on:loadedmetadata={handleLoadedMetadata}
		on:play={handleAudioPlay}
		on:pause={handleAudioPause}
	></audio>

	<div
		class="flex items-center justify-between gap-3 text-sm text-base-content/70"
	>
		<span>{formatTime(currentTime)}</span>
		<span>{formatTime(duration)}</span>
	</div>

	{#if Array.isArray(waveforms) && waveforms.length}
		<div
			class="relative border border-base-300 rounded-lg bg-base-100 overflow-hidden min-h-20 cursor-pointer"
			bind:this={waveformRef}
			role="slider"
			aria-label="Audio progress"
			aria-valuemin="0"
			aria-valuemax="100"
			aria-valuenow={Math.round(progress * 100) || 0}
			tabindex="0"
			on:pointerdown|preventDefault={handleSeekStart}
		>
			<img
				src={waveforms?.[0]}
				alt="Primary waveform"
				class="absolute inset-0 w-full h-full object-cover opacity-60"
				loading="lazy"
			/>
			{#if waveforms?.[1]}
				<img
					src={waveforms[1]}
					alt="Secondary waveform"
					class="absolute inset-0 w-full h-full object-cover"
					loading="lazy"
					style={`clip-path: inset(0 ${(1 - progress) * 100}% 0 0); opacity: ${isPlaying ? 0.95 : 0.6}; transition: clip-path 120ms linear;`}
				/>
			{/if}
			<div class="absolute inset-y-0" style={`left: ${progress * 100}%;`}>
				<div
					class="h-full w-0.5 bg-primary/80 transform -translate-x-1/2"
				></div>
				<div
					class="w-3 h-3 rounded-full bg-primary/90 border border-base-100 transform -translate-x-1/2 -translate-y-1/2 absolute top-1/2"
				></div>
			</div>
			<div
				class="absolute bottom-1 right-2 text-[0.65rem] font-semibold bg-base-100/80 px-2 py-0.5 rounded-full"
			>
				{Math.round(progress * 100) || 0}%
			</div>
		</div>

		<div
			class="flex items-center justify-center gap-4 pt-2"
			role="group"
			aria-label="Audio playback controls"
		>
			<button
				type="button"
				class="btn btn-sm btn-ghost"
				on:click={() => jumpBy(-10)}
				title="Rewind 10 seconds"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					fill="none"
					viewBox="0 0 67 67"
					><path
						fill="#fff"
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M33.5 0C52 0 67 15 67 33.5S52 67 33.5 67 0 52 0 33.5c.03-1.4 1.17-2.53 2.58-2.53 1.4 0 2.55 1.13 2.57 2.53 0 15.65 12.7 28.35 28.35 28.35 15.66 0 28.35-12.7 28.35-28.35 0-15.66-12.69-28.35-28.35-28.35h-.04c-7 0-13.76 2.61-18.94 7.3-.46.42-.91.85-1.34 1.29h6.58c1.42 0 2.57 1.16 2.57 2.58 0 1.42-1.15 2.58-2.57 2.58H6.01c-1.42 0-2.57-1.16-2.57-2.58V2.58C3.44 1.15 4.59 0 6.01 0c1.43 0 2.58 1.15 2.58 2.58v8.52c.78-.86 1.61-1.7 2.47-2.47A33.407 33.407 0 0 1 33.46 0h.04zm.48 41.34c-1.6-2.21-2-5.2-2-7.85 0-2.65.4-5.63 2-7.83 1.44-1.97 3.47-2.84 5.88-2.84 2.41 0 4.42.87 5.86 2.84 1.61 2.21 2.03 5.16 2.03 7.83 0 2.66-.4 5.64-2 7.85-1.43 1.97-3.47 2.84-5.89 2.84-2.41 0-4.45-.86-5.88-2.84zm-9.73-12.77l-5 1.58v-4.21l5.87-2.65h4.28v20.47h-5.15V28.57zm17.61 9.96c.61-1.33.68-3.6.68-5.04s-.07-3.7-.68-5.02c-.4-.86-1.04-1.29-2-1.29-.95 0-1.59.42-1.99 1.29-.61 1.32-.68 3.58-.68 5.02 0 1.44.07 3.71.68 5.04.4.87 1.04 1.29 1.99 1.29.96 0 1.6-.42 2-1.29z"
					/></svg
				>
			</button>
			<button
				type="button"
				class="btn btn-sm btn-primary"
				on:click={playPause}
				title={isPlaying ? "Pause" : "Play"}
			>
				<SvelteFa
					icon={isPlaying ? faPauseCircle : faPlayCircle}
					size="2x"
				/>
			</button>
			<button
				type="button"
				class="btn btn-sm btn-ghost"
				on:click={() => jumpBy(10)}
				title="Forward 10 seconds"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					fill="none"
					viewBox="0 0 67 67"
					><path
						fill="#fff"
						fill-rule="evenodd"
						d="M33.5 0C15 0 0 15 0 33.5S15 67 33.5 67 67 52 67 33.5a2.583 2.583 0 0 0-2.58-2.53c-1.4 0-2.55 1.13-2.57 2.53 0 15.66-12.69 28.35-28.35 28.35-15.65 0-28.35-12.7-28.35-28.35 0-15.66 12.7-28.35 28.35-28.35 7.3 0 13.96 2.76 18.99 7.3.46.42.9.85 1.34 1.29h-6.59a2.58 2.58 0 0 0 0 5.16h13.75c1.42 0 2.57-1.16 2.57-2.58V2.58c0-1.43-1.15-2.58-2.57-2.58-1.43 0-2.58 1.15-2.58 2.58v8.52c-.78-.87-1.61-1.7-2.47-2.48A33.446 33.446 0 0 0 33.54 0h-.04zm.48 41.34c-1.6-2.21-2-5.2-2-7.85 0-2.65.4-5.63 2-7.83 1.44-1.97 3.47-2.84 5.88-2.84 2.41 0 4.42.87 5.86 2.84 1.61 2.21 2.03 5.16 2.03 7.83 0 2.66-.4 5.64-2 7.85-1.43 1.97-3.47 2.84-5.89 2.84-2.41 0-4.45-.87-5.88-2.84zm-9.73-12.77l-5 1.58v-4.21l5.87-2.65h4.28v20.47h-5.15V28.57zm17.61 9.96c.61-1.33.68-3.6.68-5.04s-.07-3.7-.68-5.02c-.4-.87-1.04-1.29-2-1.29-.95 0-1.59.42-1.99 1.29-.61 1.32-.68 3.58-.68 5.02 0 1.44.07 3.71.68 5.04.4.86 1.04 1.28 1.99 1.28.96 0 1.6-.42 2-1.28z"
					/></svg
				>
			</button>
		</div>
	{:else}
		<div
			class="relative h-2 rounded-full bg-base-300 cursor-pointer"
			bind:this={waveformRef}
			role="slider"
			aria-label="Audio progress"
			aria-valuemin="0"
			aria-valuemax="100"
			aria-valuenow={Math.round(progress * 100) || 0}
			tabindex="0"
			on:pointerdown|preventDefault={handleSeekStart}
		>
			<div
				class="absolute inset-y-0 rounded-full bg-primary/40"
				style={`width: ${progress * 100}%;`}
			></div>
			<div
				class="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary border border-base-100"
				style={`left: ${progress * 100}%; transform: translate(-50%, -50%);`}
			></div>
		</div>
		<div
			class="flex items-center justify-center gap-4 pt-2"
			role="group"
			aria-label="Audio playback controls"
		>
			<button
				type="button"
				class="btn btn-sm btn-ghost"
				on:click={() => jumpBy(-10)}
				title="Rewind 10 seconds"
			>
				<SvelteFa icon={faBackwardStep} />
			</button>
			<button
				type="button"
				class="btn btn-sm btn-primary"
				on:click={playPause}
				title={isPlaying ? "Pause" : "Play"}
			>
				<SvelteFa
					icon={isPlaying ? faPauseCircle : faPlayCircle}
					size="2x"
				/>
			</button>
			<button
				type="button"
				class="btn btn-sm btn-ghost"
				on:click={() => jumpBy(10)}
				title="Forward 10 seconds"
			>
				<SvelteFa icon={faForwardStep} />
			</button>
		</div>
	{/if}

	{#if Array.isArray(transcript) && transcript.length}
		<div
			class="chat chat-start flex flex-col gap-3 max-h-48 overflow-y-auto"
			bind:this={transcriptContainer}
		>
			{#each transcript as segment, segIndex}
				<div
					class={`${getBubbleClasses(segment.speaker)} ${activeSegmentIndex === segIndex ? "active-segment" : ""}`}
					bind:this={segmentRefs[segIndex]}
				>
					<div
						class={`${getBubbleInnerClasses(segment.speaker)} ${activeSegmentIndex === segIndex ? "ring ring-primary ring-offset-2" : ""}`}
					>
						<div
							class="flex items-center justify-between gap-4 text-xs font-semibold uppercase tracking-wide"
						>
							<span>{formatSpeaker(segment.speaker)}</span>
							{#if typeof segment.start === "number"}
								<span>{formatSegmentTime(segment.start)}</span>
							{/if}
						</div>
						<p class="mt-1 text-sm whitespace-pre-wrap">
							{segment.phrase || ""}
						</p>
					</div>
				</div>
			{/each}
		</div>
	{:else}
		<p class="text-sm text-base-content/70">
			No transcript segments were captured for this call.
		</p>
	{/if}
</div>
